// RRManualMatchEditor — create or edit a match structurally.
// RR Update 2 (Feb 2026). Distinct from RRMatchDialog which records
// results — this one handles pool / teams / round / court / start time.
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Trash, Check, X } from '@phosphor-icons/react';
import { roundRobinAPI } from '../../lib/roundRobinApi';
import { isoToEventParts, localPartsToIso } from '../../lib/scheduleTime';

function fmt(err, fallback) {
  const d = err?.response?.data?.detail;
  const msg = typeof d === 'string' ? d : (d?.message || fallback);
  toast.error(msg);
}

const isoToParts = isoToEventParts;
function partsToIso(date, time) {
  if (!date && !time) return null;
  const d = date || new Date().toISOString().slice(0, 10);
  return localPartsToIso(d, time || '00:00');
}

export default function RRManualMatchEditor({ rr, open, match, onClose, onSaved }) {
  const isEdit = !!match?.id;
  const numPools = rr.num_pools || 1;
  const labels = rr.pool_labels || {};
  const poolLabel = (pn) => labels[String(pn)] || `Pool ${pn}`;
  const [pool, setPool] = useState(1);
  const [e1, setE1] = useState('');
  const [e2, setE2] = useState('');
  const [round, setRound] = useState('');
  const [court, setCourt] = useState('');
  const [dateStr, setDateStr] = useState('');
  const [timeStr, setTimeStr] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (isEdit) {
      setPool(match.pool_number ?? 1);
      setE1(match.entry1?.id ?? '');
      setE2(match.entry2?.id ?? '');
      setRound(match.round_number ?? '');
      setCourt(match.court_label ?? '');
      const parts = isoToParts(match.scheduled_at);
      setDateStr(parts.date); setTimeStr(parts.time);
    } else {
      setPool(1); setE1(''); setE2(''); setRound(''); setCourt(''); setDateStr(''); setTimeStr('');
    }
  }, [open, isEdit, match]);

  // Only offer entries currently in the chosen pool for A/B pickers.
  const eligible = useMemo(
    () => (rr.entries || []).filter((e) => e.pool_number === pool),
    [rr.entries, pool]
  );

  if (!open) return null;

  const submit = async () => {
    if (!e1 || !e2) return toast.error('Pick both teams.');
    if (e1 === e2) return toast.error('A match needs two different teams.');
    setBusy(true);
    try {
      const payload = {
        pool_number: Number(pool),
        entry1_id: e1,
        entry2_id: e2,
        round_number: round === '' || round == null ? null : Number(round),
        court_label: court.trim() || null,
        scheduled_at: partsToIso(dateStr, timeStr),
      };
      if (isEdit) await roundRobinAPI.editMatch(rr.id, match.id, payload);
      else        await roundRobinAPI.createMatch(rr.id, payload);
      toast.success(isEdit ? 'Match updated.' : 'Match created.');
      onSaved?.(); onClose?.();
    } catch (e) { fmt(e, 'Save failed.'); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    if (!isEdit) return;
    if (!window.confirm('Delete this match?')) return;
    setBusy(true);
    try {
      await roundRobinAPI.deleteMatch(rr.id, match.id);
      toast.success('Match deleted.'); onSaved?.(); onClose?.();
    } catch (e) { fmt(e, 'Delete failed.'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-4" data-testid="rr-manual-match-editor">
      <div className="bg-white rounded-2xl max-w-md w-full max-h-[92vh] overflow-y-auto">
        <div className="px-4 py-3 border-b border-[#e3dcd1] sticky top-0 bg-white flex items-center justify-between">
          <div className="text-sm font-medium text-[#013523]">{isEdit ? 'Edit match' : 'New match'}</div>
          <button onClick={onClose} className="text-[#9aa39d]" data-testid="rrmme-close"><X size={16} /></button>
        </div>
        <div className="p-4 space-y-3 text-sm">
          <label className="block">
            <span className="text-[11px] uppercase tracking-wider text-[#6a766e]">Pool</span>
            <select
              value={pool}
              onChange={(e) => setPool(Number(e.target.value))}
              data-testid="rrmme-pool"
              className="mt-1 w-full px-3 py-2 border border-[#e3dcd1] rounded-lg bg-white text-sm"
            >
              {Array.from({ length: numPools }, (_, i) => i + 1).map((pn) => (
                <option key={pn} value={pn}>{poolLabel(pn)}</option>
              ))}
            </select>
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <label className="block">
              <span className="text-[11px] uppercase tracking-wider text-[#6a766e]">Team A</span>
              <select value={e1} onChange={(e) => setE1(e.target.value)} data-testid="rrmme-entry1" className="mt-1 w-full px-3 py-2 border border-[#e3dcd1] rounded-lg bg-white text-sm">
                <option value="">Pick…</option>
                {eligible.map((e) => <option key={e.id} value={e.id}>{e.display_name}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-wider text-[#6a766e]">Team B</span>
              <select value={e2} onChange={(e) => setE2(e.target.value)} data-testid="rrmme-entry2" className="mt-1 w-full px-3 py-2 border border-[#e3dcd1] rounded-lg bg-white text-sm">
                <option value="">Pick…</option>
                {eligible.map((e) => <option key={e.id} value={e.id}>{e.display_name}</option>)}
              </select>
            </label>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <label className="block">
              <span className="text-[11px] uppercase tracking-wider text-[#6a766e]">Round</span>
              <input type="number" min={1} max={99} value={round} onChange={(e) => setRound(e.target.value)} data-testid="rrmme-round" className="mt-1 w-full px-3 py-2 border border-[#e3dcd1] rounded-lg text-sm" placeholder="1" />
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-wider text-[#6a766e]">Date</span>
              <input type="date" value={dateStr} onChange={(e) => setDateStr(e.target.value)} data-testid="rrmme-date" className="mt-1 w-full px-3 py-2 border border-[#e3dcd1] rounded-lg text-sm" />
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-wider text-[#6a766e]">Time</span>
              <input type="time" value={timeStr} onChange={(e) => setTimeStr(e.target.value)} data-testid="rrmme-time" className="mt-1 w-full px-3 py-2 border border-[#e3dcd1] rounded-lg text-sm" />
            </label>
          </div>
          <label className="block">
            <span className="text-[11px] uppercase tracking-wider text-[#6a766e]">Court</span>
            {(rr.court_labels && rr.court_labels.length > 0) ? (
              <select
                value={court} onChange={(e) => setCourt(e.target.value)}
                data-testid="rrmme-court"
                className="mt-1 w-full px-3 py-2 border border-[#e3dcd1] rounded-lg bg-white text-sm"
              >
                <option value="">— Unassigned</option>
                {rr.court_labels.map((c) => <option key={c} value={c}>Court {c}</option>)}
              </select>
            ) : (
              <input type="text" value={court} onChange={(e) => setCourt(e.target.value)} data-testid="rrmme-court" placeholder="e.g. Court 3" className="mt-1 w-full px-3 py-2 border border-[#e3dcd1] rounded-lg text-sm" />
            )}
          </label>
        </div>
        <div className="px-4 py-3 border-t border-[#e3dcd1] flex justify-between sticky bottom-0 bg-white">
          {isEdit ? (
            <button onClick={remove} disabled={busy} data-testid="rrmme-delete" className="px-3 py-1.5 rounded-full border border-red-300 text-red-700 text-xs flex items-center gap-1"><Trash size={12} /> Delete</button>
          ) : <span />}
          <div className="flex gap-2">
            <button onClick={onClose} className="px-3 py-1.5 rounded-full border border-[#e3dcd1] text-xs text-[#013523]" data-testid="rrmme-cancel">Cancel</button>
            <button onClick={submit} disabled={busy} data-testid="rrmme-save" className="px-3 py-1.5 rounded-full bg-[#013523] text-white text-xs flex items-center gap-1 disabled:opacity-60">
              <Check size={12} weight="bold" /> {isEdit ? 'Save match' : 'Create match'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
